import { TransactionState } from '@nftx/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Action } from './types';

type ActionListContextType = {
  currentIndex: number;
  currentStatus: TransactionState;
  currentError: any;
  retryRequested: boolean;
  setCurrent: (action: Action) => void;
  next: () => void;
  retry: () => void;
};

const ActionListContext = createContext<ActionListContextType>(null as any);

export const ActionListContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<TransactionState>('None');
  const [currentError, setCurrentError] = useState<any>(null);
  const [retryRequested, setRetryRequested] = useState(false);

  const next = useCallback(() => {
    setCurrentIndex((index) => index + 1);
  }, []);
  const setCurrent = useCallback(
    (action: Action) => {
      if (currentIndex !== action.index) {
        setCurrentIndex(action.index);
      }
      if (currentStatus !== action.status) {
        setCurrentStatus(action.status);
      }
      if (currentError !== action.error) {
        setCurrentError(action.error);
      }
    },
    [currentError, currentIndex, currentStatus]
  );
  const retry = useCallback(() => {
    setRetryRequested(true);
  }, []);

  const value = useMemo(() => {
    return {
      currentIndex,
      currentStatus,
      currentError,
      retryRequested,
      setCurrent,
      next,
      retry,
    };
  }, [
    currentError,
    currentIndex,
    currentStatus,
    next,
    retry,
    retryRequested,
    setCurrent,
  ]);

  useEffect(() => {
    setRetryRequested(false);
  }, [currentStatus, currentIndex]);

  return (
    <ActionListContext.Provider value={value}>
      {children}
    </ActionListContext.Provider>
  );
};

export const useActionListContext = () => useContext(ActionListContext);
